import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Menu, MenuItem, Chip, Typography, Paper, TextField } from '@mui/material';
import { displayMappingValue } from '../../../utils/headerPaths';

const BINARY_OPERATORS = ['+', '-', '*', '/'];

// The grammar the backend evaluates (plain arithmetic over the mapped columns; `abs`
// is a prefix that takes one variable or one parenthesised group):
//   expr := term (('+' | '-' | '*' | '/') term)*
//   term := variable | '(' expr ')' | 'abs' variable | 'abs' '(' expr ')'
// Returns the first violation as a message, or null. An empty formula is not a
// syntax error here — whether a mapping may be left empty is the caller's rule.
// Consumers gate on this: a formula that fails it would be written to the unit as
// Ready and only then fail during processing.
export const formulaSyntaxError = (elements) => {
  if (!elements || elements.length === 0) return null;
  let pos = 0;
  let failure = null;
  const fail = (message) => { failure = failure || message; return false; };
  const peek = () => elements[pos];
  const isOperator = (el, value) => el?.type === 'operator' && el.value === value;
  const describe = (el) => `'${el.value}'`;
  const endMessage = 'Formula cannot end with an operator (except closing parenthesis)';

  const parseTerm = () => {
    const el = peek();
    if (!el) return fail(endMessage);
    if (el.type === 'variable') { pos += 1; return true; }
    if (isOperator(el, '(')) {
      pos += 1;
      if (!parseExpr()) return false;
      if (!isOperator(peek(), ')')) return fail('Mismatched parentheses');
      pos += 1;
      return true;
    }
    if (isOperator(el, 'abs')) {
      pos += 1;
      const next = peek();
      if (!next) return fail(endMessage);
      if (next.type === 'variable' || isOperator(next, '(')) return parseTerm();
      return fail(`Expected a variable or '(' after 'abs', not ${describe(next)}`);
    }
    if (pos === 0) return fail('Formula cannot start with an operator (except opening parenthesis or abs)');
    return fail(`Expected a variable or '(' after ${describe(elements[pos - 1])}, not ${describe(el)}`);
  };
  const parseExpr = () => {
    if (!parseTerm()) return false;
    while (peek()?.type === 'operator' && BINARY_OPERATORS.includes(peek().value)) {
      pos += 1;
      if (!parseTerm()) return false;
    }
    return true;
  };

  if (!parseExpr()) return failure;
  if (pos < elements.length) {
    const el = peek();
    if (isOperator(el, ')')) return 'Mismatched parentheses';
    return `Expected an operator before ${describe(el)}`;
  }
  return null;
};

// Props: elements (the formula, as { type: 'variable' | 'operator', value } in order),
// onChange(elements), availableVariables, and disabled: while it is true nothing can be added
// to the formula or removed from it. A builder is enabled unless its caller disables it.
const FormulaBuilder = ({ elements, onChange, availableVariables, disabled = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuType, setMenuType] = useState(null); // 'variable' or 'operator'
  const [activeButton, setActiveButton] = useState(null);
  const [variableFilter, setVariableFilter] = useState('');
  const error = formulaSyntaxError(elements) || '';

  // Include prefix operator 'abs' (absolute value)
  const operators = ['+', '-', '*', '/', '(', ')', 'abs'];


  const handleAddClick = (type) => (event) => {
    setMenuType(type);
    setAnchorEl(event.currentTarget); // Anchor menu to the button clicked
    setActiveButton(type); // Keep track of which button opened the menu
  };

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setMenuType(null);
    setActiveButton(null);
    setVariableFilter('');
  }, []);

  // A builder disabled while its menu is open closes the menu, so nothing can be added from
  // it, and the menu does not open again by itself when the builder is enabled again.
  useEffect(() => {
    if (disabled) handleMenuClose();
  }, [disabled, handleMenuClose]);

  // Case-insensitive substring match on the label shown in the menu, mirroring Autocomplete's
  // default filter. An empty filter matches everything, so the list opens as it always has.
  const filteredVariables = availableVariables.filter(
    (variable) => displayMappingValue(variable).toLowerCase().includes(variableFilter.toLowerCase()),
  );

  const handleSelect = (value) => {
    // --- Selection validation logic (remains the same) ---
    if (elements.length > 0) {
        const lastElement = elements[elements.length - 1];
        if (lastElement.type === 'variable' && menuType === 'variable') {
             handleMenuClose(); return;
        }
    } else {
         // At start, allow '(' or 'abs'
         if (menuType === 'operator' && !['(', 'abs'].includes(value)) {
             handleMenuClose(); return;
         }
    }

    onChange([...elements, { type: menuType, value }]);
    handleMenuClose();
  };

  const handleDelete = (index) => {
    onChange(elements.filter((_, i) => i !== index));
  };

  return (
    <Box> {/* Outer container for layout + error */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}> {/* Main flex container: Formula | Buttons */}

        {/* Formula Display Area (Left Side) */}
        <Paper
          variant="outlined"
          sx={{
            p: 1,
            flexGrow: 1, // Takes available horizontal space
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.5,
            alignItems: 'center',
            minHeight: '38px',
            '&:hover': {
              borderColor: 'rgba(0, 0, 0, 0.87)',
            },
          }}
        >
          {elements.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', pl: 1 }}>
              Build formula...
            </Typography>
          )}
          {elements.map((el, index) => (
            <Chip
              key={index}
              label={displayMappingValue(el.value)}
              // A disabled builder's elements are shown without their delete.
              onDelete={disabled ? undefined : () => handleDelete(index)}
              color={el.type === 'operator' ? 'primary' : 'default'}
              size="small"
            />
          ))}
        </Paper>

        {/* Buttons Area (Right Side - Stacked Vertically) */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={disabled}
            onClick={handleAddClick('variable')}
            aria-controls={activeButton === 'variable' ? 'add-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={activeButton === 'variable' ? 'true' : undefined}
          >
            Add Variable
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={disabled}
            onClick={handleAddClick('operator')}
            aria-controls={activeButton === 'operator' ? 'add-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={activeButton === 'operator' ? 'true' : undefined}
          >
            Add Operator
          </Button>
        </Box>

        {/* Menu - anchored to the clicked button */}
        <Menu
          id="add-menu" // Added ID for aria-controls
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          // The variable menu hands focus to its search box instead of the first item: the
          // list's own auto-focus would take focus straight back off the input. The operator
          // menu is short and unfiltered, so it keeps the default behaviour.
          autoFocus={menuType !== 'variable'}
          MenuListProps={{
            'aria-labelledby': activeButton ? `${activeButton}-button` : undefined, // Accessibility
            // The list's top padding would otherwise show items sliding past above the
            // pinned search box.
            ...(menuType === 'variable' ? { sx: { pt: 0 } } : {}),
          }}
        >
          {menuType === 'variable' && (
            <Box
              // The menu's paper is the scroller, so the search box is stuck to the top of it
              // and stays put while a long variable list scrolls underneath.
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1,
                minWidth: 240,
                px: 1.5,
                py: 1,
                backgroundColor: 'background.paper',
              }}
            >
              <TextField
                autoFocus
                fullWidth
                size="small"
                placeholder="Search variables..."
                value={variableFilter}
                onChange={(e) => setVariableFilter(e.target.value)}
                // The menu list types-to-focus on every keystroke, which would pull focus out
                // of this input and onto a matching item — but Escape must still reach the
                // modal root, or the menu stops closing from the keyboard while the input
                // holds focus (it always does; it is autofocused).
                onKeyDown={(e) => {
                  if (e.key !== 'Escape') e.stopPropagation();
                }}
              />
            </Box>
          )}
          {menuType === 'variable' && filteredVariables.length === 0 && (
            <MenuItem disabled>No matching variables</MenuItem>
          )}
          {menuType === 'variable' &&
            filteredVariables.map((v) => (
              <MenuItem key={v} onClick={() => handleSelect(v)}>
                {displayMappingValue(v)}
              </MenuItem>
            ))}
          {menuType === 'operator' &&
            operators.map((op) => (
              <MenuItem key={op} onClick={() => handleSelect(op)}>
                {op}
              </MenuItem>
            ))}
        </Menu>

      </Box> {/* End Main flex container */}

      {/* Error Message - Placed below the flex container */}
      {error && (
        <Typography color="error" variant="caption" sx={{ mt: 1, display: 'block', pl: 1 /* Align roughly with Paper start */ }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default FormulaBuilder;
